import { useFormikContext } from "formik";
import { useEffect, useMemo, useState } from "react";
import {
  Flex,
  Box,
  Text,
  Switch,
  Spinner,
  Button,
  TextField,
  TextArea,
  Select,
} from "@radix-ui/themes";
import SearchableSelect from "@/components/ui/SearchableSelect";
import ExpandableTextField from "@/components/form/ExpandableTextField";
import { useQuery } from "@tanstack/react-query";
import { object, string } from "yup";
import { IntegrationDefinition } from "@/types/breeze/Integration";
import { WorkflowStep } from "@/types/breeze/Workflow";
import { useAuth } from "@/providers/AuthProvider";
import { integrationService } from "@/services/IntegrationService";
import { BaseService } from "@/services/BaseService";
import toast from "react-hot-toast";
import { CheckCircleIcon, AlertCircleIcon } from "lucide-react";
import VariableMenu from "./shared/VariableMenu";

const AI_PROVIDER_IDS = ["claude", "openai", "grok", "gemini"];
class AiModelService extends BaseService {
  getModels = (provider?: string) =>
    this.getRequest<
      Array<{ id: string; name: string; badge: string; provider: string }>
    >(
      provider
        ? `/integrations/ai/models?provider=${provider}`
        : "/integrations/ai/models",
    );
}
const aiModelService = new AiModelService();

export const integrationValidationSchema = object({
  parameters: object({
    provider: string().required(),
    operation: string().required(),
    credential_source: string().oneOf([
      "system",
      "current_user",
      "connection",
      "entity_field",
    ]),
    credential_field: string().when("credential_source", {
      is: "entity_field",
      then: (s) => s.required(),
      otherwise: (s) => s.optional(),
    }),
  }),
});

interface Props {
  definition: IntegrationDefinition;
  existingSteps?: WorkflowStep[];
  triggerEntity?: string;
}

export default function IntegrationFields({ definition, existingSteps = [], triggerEntity }: Props) {
  const { values, setFieldValue } = useFormikContext<any>();
  const { currentTenant } = useAuth();
  const tenantId = currentTenant?.id || 0;

  const operation = values.parameters?.operation;
  const opDef = definition.operations.find((o: any) => o.key === operation);

  // Check connection status
  const { data: connections = [] } = useQuery({
    queryKey: ["integration-connections", tenantId, definition.provider],
    queryFn: () =>
      integrationService.getConnectionsForProvider(definition.provider),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const hasConnection = connections.some((c: any) => c.is_enabled !== false);

  // Check OAuth settings — for child providers, also check parent
  const oauthProvider = definition.parent_provider || definition.provider;
  const { data: oauthSettings, isLoading: isLoadingOAuth } = useQuery({
    queryKey: ["integration-oauth", tenantId, oauthProvider],
    queryFn: () =>
      integrationService.getIntegrationOAuthSettings(oauthProvider),
    staleTime: 0,
    refetchOnMount: true,
    refetchOnWindowFocus: true,
  });

  const isOAuthConfigured = oauthSettings?.has_client_id;

  // Set default operation if none selected
  useEffect(() => {
    if (!operation && definition.operations.length > 0) {
      setFieldValue("parameters.operation", definition.operations[0].key);
    }
  }, [operation, definition.operations, setFieldValue]);

  return (
    <Flex direction="column" gap="4">
      {/* Connection Status */}
      <ConnectionStatus
        displayName={definition.display_name}
        provider={definition.provider}
        hasConnection={hasConnection}
        isOAuthConfigured={isOAuthConfigured}
        isLoading={isLoadingOAuth}
        tenantId={tenantId}
      />

      {/* Operation */}
      <Box>
        <Text
          as="label"
          size="2"
          weight="medium"
          style={{ display: "block", marginBottom: "6px" }}
        >
          Operation <Text color="red">*</Text>
        </Text>
        <Select.Root
          value={operation || ""}
          onValueChange={(v) => {
            setFieldValue("parameters.operation", v);
            setFieldValue("parameters.inputs", {});
          }}
        >
          <Select.Trigger
            placeholder="Select operation..."
            style={{ width: "100%" }}
          />
          <Select.Content
            container={
              typeof document !== "undefined" ? document.body : undefined
            }
            style={{ zIndex: 12000 }}
          >
            {definition.operations.map((op: any) => (
              <Select.Item key={op.key} value={op.key}>
                {op.display_name || op.key}
              </Select.Item>
            ))}
          </Select.Content>
        </Select.Root>
      </Box>

      {/* Credential Source */}
      <Box>
        <Text
          as="label"
          size="2"
          weight="medium"
          style={{ display: "block", marginBottom: "6px" }}
        >
          Credential Source
        </Text>
        <Select.Root
          value={values.parameters?.credential_source || "system"}
          onValueChange={(v) =>
            setFieldValue("parameters.credential_source", v)
          }
        >
          <Select.Trigger style={{ width: "100%" }} />
          <Select.Content
            container={
              typeof document !== "undefined" ? document.body : undefined
            }
            style={{ zIndex: 12000 }}
          >
            <Select.Item value="system">System (shared connection)</Select.Item>
            <Select.Item value="current_user">
              Current User (first connection)
            </Select.Item>
            <Select.Item value="connection">
              Specific Connection (by ID)
            </Select.Item>
            <Select.Item value="entity_field">Entity Field</Select.Item>
          </Select.Content>
        </Select.Root>
        <Text size="1" color="gray" style={{ display: "block", marginTop: 4 }}>
          {values.parameters?.credential_source === "current_user"
            ? "Uses the first connection of the user who triggered the workflow."
            : values.parameters?.credential_source === "connection"
              ? "Uses a specific connection ID, typically from a record field."
              : values.parameters?.credential_source === "entity_field"
                ? "Reads credentials from a field on the triggering entity."
                : "Uses the organisation-level connection configured in Settings."}
        </Text>
      </Box>

      {values.parameters?.credential_source === "connection" && (
        <Box>
          <Text
            as="label"
            size="2"
            weight="medium"
            style={{ display: "block", marginBottom: "6px" }}
          >
            Connection ID <Text color="red">*</Text>
          </Text>
          <TextField.Root
            value={values.parameters?.connection_id || ""}
            onChange={(e) =>
              setFieldValue("parameters.connection_id", e.target.value)
            }
            placeholder="{{ $anythink.steps.read_post.data[0].connection_id }}"
          />
        </Box>
      )}

      {values.parameters?.credential_source === "entity_field" && (
        <Box>
          <Text
            as="label"
            size="2"
            weight="medium"
            style={{ display: "block", marginBottom: "6px" }}
          >
            Credential Field Path <Text color="red">*</Text>
          </Text>
          <TextField.Root
            value={values.parameters?.credential_field || ""}
            onChange={(e) =>
              setFieldValue("parameters.credential_field", e.target.value)
            }
            placeholder="{{ $anythink.trigger.data.secret_field }}"
          />
        </Box>
      )}

      {/* Operation Parameters */}
      {opDef && opDef.input_schema && (
        <Box
          style={{ borderTop: "1px solid var(--gray-6)", paddingTop: "16px" }}
        >
          <Text
            size="2"
            color="gray"
            style={{ display: "block", marginBottom: "12px" }}
          >
            {opDef.description}
          </Text>
          <OperationInputs
            schema={opDef.input_schema}
            values={values.parameters?.inputs || {}}
            onChange={(inputs) => setFieldValue("parameters.inputs", inputs)}
            tenantId={tenantId}
            provider={definition.provider}
            existingSteps={existingSteps}
            triggerEntity={triggerEntity}
          />
        </Box>
      )}
    </Flex>
  );
}

// Connection Status Component
function ConnectionStatus({
  displayName,
  provider,
  hasConnection,
  isOAuthConfigured,
  isLoading,
  tenantId,
}: {
  displayName: string;
  provider: string;
  hasConnection: boolean;
  isOAuthConfigured: boolean | undefined;
  isLoading: boolean;
  tenantId: number;
}) {
  const handleConnect = async () => {
    try {
      const callbackUrl = `${window.location.origin}/org/${tenantId}/settings/integrations/callback`;
      const url = await integrationService.getOAuthUrl(provider, callbackUrl);
      sessionStorage.setItem(
        "pendingIntegration",
        JSON.stringify({ provider, tenantId, timestamp: Date.now() }),
      );
      window.location.href = url;
    } catch (error: any) {
      toast.error(
        error?.response?.data || error?.message || "Failed to start OAuth flow",
      );
    }
  };
  if (isLoading) {
    return (
      <Box
        p="3"
        style={{ backgroundColor: "var(--gray-3)", borderRadius: "6px" }}
      >
        <Flex gap="2" align="center">
          <Spinner size="2" />
          <Text size="2" color="gray">
            Checking configuration...
          </Text>
        </Flex>
      </Box>
    );
  }

  if (!isOAuthConfigured && !hasConnection) {
    return (
      <Box
        p="3"
        style={{ backgroundColor: "var(--yellow-3)", borderRadius: "6px" }}
      >
        <Flex gap="3" align="start">
          <AlertCircleIcon
            size={18}
            color="var(--yellow-11)"
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Box style={{ flex: 1 }}>
            <Text size="2" weight="medium" style={{ display: "block" }}>
              Setup Required
            </Text>
            <Text
              size="1"
              color="gray"
              style={{ display: "block", marginTop: 2 }}
            >
              Configure {displayName} in Settings &gt; Integrations before using
              this step.
            </Text>
          </Box>
        </Flex>
      </Box>
    );
  }

  if (isOAuthConfigured && !hasConnection) {
    return (
      <Box
        p="3"
        style={{ backgroundColor: "var(--blue-3)", borderRadius: "6px" }}
      >
        <Flex gap="3" align="start">
          <CheckCircleIcon
            size={18}
            color="var(--blue-11)"
            style={{ flexShrink: 0, marginTop: 2 }}
          />
          <Box style={{ flex: 1 }}>
            <Text size="2" weight="medium" style={{ display: "block" }}>
              OAuth Configured
            </Text>
            <Text
              size="1"
              color="gray"
              style={{ display: "block", marginTop: 2 }}
            >
              Click Connect to authorize {displayName}.
            </Text>
          </Box>
          <Button
            size="1"
            variant="soft"
            style={{ flexShrink: 0 }}
            onClick={handleConnect}
          >
            Connect
          </Button>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      p="3"
      style={{ backgroundColor: "var(--green-3)", borderRadius: "6px" }}
    >
      <Flex gap="3" align="center">
        <CheckCircleIcon
          size={18}
          color="var(--green-11)"
          style={{ flexShrink: 0 }}
        />
        <Text size="2" weight="medium" color="green">
          {displayName} Connected
        </Text>
      </Flex>
    </Box>
  );
}

// OAuth Configuration Dialog
// Component to render operation input fields based on JSON schema
function OperationInputs({
  schema,
  values,
  onChange,
  tenantId,
  provider,
  existingSteps,
  triggerEntity,
}: {
  schema: any;
  values: Record<string, any>;
  onChange: (values: Record<string, any>) => void;
  tenantId: number;
  provider: string;
  existingSteps: WorkflowStep[];
  triggerEntity?: string;
}) {
  const [variableFields, setVariableFields] = useState<Record<string, boolean>>(
    () => {
      // Auto-detect fields that already have template variables
      const initial: Record<string, boolean> = {};
      for (const [name, val] of Object.entries(values)) {
        if (typeof val === "string" && val.includes("{{")) initial[name] = true;
      }
      return initial;
    },
  );

  // Fetch available AI models for model field dropdown
  const isAiProvider = AI_PROVIDER_IDS.includes(provider);
  const { data: aiModels = [] } = useQuery({
    queryKey: ["ai-models-integration", provider],
    queryFn: () => aiModelService.getModels(provider),
    staleTime: 5 * 60_000,
    enabled: isAiProvider,
  });
  const providerModels = useMemo(
    () =>
      aiModels
        .filter((m) => m.provider === provider)
        .map((m) => ({ value: m.id, label: m.name })),
    [aiModels, provider],
  );

  // Use propertyOrder from schema if available, otherwise fall back to Object.keys order
  const fieldEntries = useMemo(() => {
    if (!schema?.properties) return [];
    const entries = Object.entries(schema.properties) as [string, any][];
    if (schema.propertyOrder?.length) {
      const orderMap = new Map<string, number>(
        schema.propertyOrder.map((k: string, i: number) => [k, i]),
      );
      return entries.sort((a, b) => {
        const aIdx = orderMap.get(a[0]) ?? 999;
        const bIdx = orderMap.get(b[0]) ?? 999;
        return aIdx - bIdx;
      });
    }
    return entries;
  }, [schema]);

  if (!schema || !schema.properties) {
    return (
      <Text size="2" color="red">
        No parameters available
      </Text>
    );
  }

  const handleChange = (name: string, value: any) => {
    onChange({ ...values, [name]: value });
  };

  const toggleVariableMode = (name: string) => {
    setVariableFields((prev) => ({ ...prev, [name]: !prev[name] }));
  };

  // Fields that have a structured input (dropdown, datetime, etc.) and should offer variable toggle
  const hasStructuredInput = (name: string, prop: any) =>
    (prop.type === "string" && prop.format === "date-time") ||
    (prop.type === "string" && (name === "timeZone" || name === "time_zone")) ||
    (prop.type === "string" && prop.enum) ||
    prop.type === "boolean";

  return (
    <Flex direction="column" gap="4">
      {fieldEntries.map(([name, prop]: [string, any]) => {
        const expandableFields = [
          "prompt",
          "system_prompt",
          "text",
          "content",
          "description",
        ];
        const isExpandable =
          prop.type === "string" &&
          !prop.enum &&
          expandableFields.includes(name);
        const multiline =
          (name === "text" || name === "description") && !isExpandable;
        const isDynamicField =
          prop.type === "string" &&
          !prop.enum &&
          (name === "channel" || name === "user" || prop.dynamic === true);
        const isVariable = variableFields[name];
        const showToggle = hasStructuredInput(name, prop);

        // Expandable text fields get their own component with built-in variable toggle and fullscreen
        if (isExpandable) {
          return (
            <Box key={name}>
              <ExpandableTextField
                value={values[name] || ""}
                onChange={(v) => handleChange(name, v)}
                label={prop.title || name}
                placeholder={prop.description}
                rows={name === "prompt" || name === "content" ? 5 : 3}
                mode="plain"
                variableToggle
                required={schema.required?.includes(name)}
                variablePicker={
                  <VariableMenu
                    existingSteps={existingSteps}
                    triggerEntity={triggerEntity}
                    onInsert={(t) => {
                      const base = (values[name] as string) || "";
                      const sep = base && !base.endsWith(" ") ? " " : "";
                      handleChange(name, `${base}${sep}${t}`);
                    }}
                  />
                }
              />
            </Box>
          );
        }

        return (
          <Box key={name}>
            <Flex
              justify="between"
              align="center"
              style={{ marginBottom: "6px" }}
            >
              <Text as="label" size="2" weight="medium">
                {prop.title || name}
                {schema.required?.includes(name) && <Text color="red"> *</Text>}
              </Text>
              {showToggle && (
                <Button
                  type="button"
                  variant="ghost"
                  size="1"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    toggleVariableMode(name);
                  }}
                  style={{ fontSize: "11px", padding: "2px 6px" }}
                >
                  {isVariable ? "Use picker" : "Use variable"}
                </Button>
              )}
            </Flex>

            {isVariable ? (
              <TextField.Root
                value={values[name] || ""}
                onChange={(e) => handleChange(name, e.target.value)}
                placeholder="{{step_name.output.field}}"
              />
            ) : prop.type === "string" && prop.format === "date-time" ? (
              <TextField.Root
                type="datetime-local"
                value={values[name] || ""}
                onChange={(e) => handleChange(name, e.target.value)}
              />
            ) : prop.type === "string" &&
              (name === "timeZone" || name === "time_zone") ? (
              <TimezoneSelect
                value={values[name] || prop.default || "UTC"}
                onChange={(v: string) => handleChange(name, v)}
              />
            ) : prop.type === "string" && prop.enum ? (
              <Select.Root
                value={values[name] || prop.default || ""}
                onValueChange={(v) => handleChange(name, v)}
              >
                <Select.Trigger style={{ width: "100%" }} />
                <Select.Content
                  container={
                    typeof document !== "undefined" ? document.body : undefined
                  }
                  style={{ zIndex: 12000 }}
                >
                  {prop.enum.map((val: string) => (
                    <Select.Item key={val} value={val}>
                      {val}
                    </Select.Item>
                  ))}
                </Select.Content>
              </Select.Root>
            ) : name === "model" &&
              isAiProvider &&
              providerModels.length > 0 ? (
              <SearchableSelect
                value={values[name] || prop.default || ""}
                onChange={(v) => handleChange(name, v)}
                options={providerModels}
                placeholder="Select model..."
                searchPlaceholder="Search models..."
              />
            ) : isDynamicField ? (
              <DynamicFieldSelect
                tenantId={tenantId}
                provider={provider}
                field={name}
                value={values[name] || ""}
                onChange={(v) => handleChange(name, v)}
                placeholder={prop.description}
              />
            ) : prop.type === "boolean" ? (
              <Flex align="center" gap="2">
                <Switch
                  checked={values[name] ?? prop.default ?? false}
                  onCheckedChange={(checked) => handleChange(name, checked)}
                />
                {prop.description && (
                  <Text size="2" color="gray">
                    {prop.description}
                  </Text>
                )}
              </Flex>
            ) : prop.type === "array" ? (
              <ArrayInput
                values={values[name] || []}
                onChange={(v) => handleChange(name, v)}
                placeholder={prop.description}
              />
            ) : multiline ? (
              <TextArea
                value={values[name] || ""}
                onChange={(e) => handleChange(name, e.target.value)}
                placeholder={prop.description}
                rows={3}
              />
            ) : (
              <TextField.Root
                value={values[name] || ""}
                onChange={(e) => handleChange(name, e.target.value)}
                placeholder={prop.description}
                autoComplete="one-time-code"
              />
            )}
          </Box>
        );
      })}
    </Flex>
  );
}

// Dynamic field select component
function DynamicFieldSelect({
  tenantId,
  provider,
  field,
  value,
  onChange,
  placeholder,
}: {
  tenantId: number;
  provider: string;
  field: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}) {
  const {
    data: options = [],
    isLoading,
    error,
  } = useQuery({
    queryKey: ["integration-field-options", tenantId, provider, field],
    queryFn: () => integrationService.getFieldOptions(provider, field),
    enabled: !!tenantId && !!provider,
    retry: false,
  });

  if (isLoading) {
    return (
      <Flex align="center" gap="2" style={{ padding: "8px" }}>
        <Spinner size="2" />
        <Text size="2" color="gray">
          Loading {field}s...
        </Text>
      </Flex>
    );
  }

  if (error) {
    const errorMsg =
      (error as any)?.response?.data?.error || `Could not load ${field}s`;
    return (
      <Flex direction="column" gap="2">
        <Text size="1" color="red">
          {errorMsg}
        </Text>
        <TextField.Root
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder || `Enter ${field} ID`}
          autoComplete="one-time-code"
        />
      </Flex>
    );
  }

  if (options.length === 0) {
    return (
      <TextField.Root
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder || `Enter ${field} ID`}
        autoComplete="one-time-code"
      />
    );
  }

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder || `Select ${field}...`}
      searchPlaceholder={`Search ${field}s...`}
    />
  );
}

// Array input component
function ArrayInput({
  values,
  onChange,
  placeholder,
}: {
  values: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}) {
  const handleAdd = () => onChange([...values, ""]);
  const handleRemove = (index: number) => {
    onChange(values.filter((_, i) => i !== index));
  };
  const handleChange = (index: number, value: string) => {
    const newValues = [...values];
    newValues[index] = value;
    onChange(newValues);
  };

  return (
    <Flex direction="column" gap="2">
      {values.map((val, idx) => (
        <Flex key={idx} gap="2" align="center">
          <Box style={{ flex: 1 }}>
            <TextField.Root
              value={val}
              onChange={(e) => handleChange(idx, e.target.value)}
              placeholder={placeholder}
            />
          </Box>
          <Button
            type="button"
            size="1"
            variant="soft"
            color="red"
            onClick={() => handleRemove(idx)}
          >
            Remove
          </Button>
        </Flex>
      ))}
      <Button
        type="button"
        size="1"
        variant="soft"
        onClick={handleAdd}
        style={{ alignSelf: "flex-start" }}
      >
        + Add Item
      </Button>
    </Flex>
  );
}

// Timezone select using Intl API + shared SearchableSelect
function TimezoneSelect({
  value,
  onChange,
}: {
  value: string;
  onChange: (value: string) => void;
}) {
  const timezones = useMemo(() => {
    const now = new Date();
    return Intl.supportedValuesOf("timeZone").map((tz) => {
      const offset =
        new Intl.DateTimeFormat("en-US", {
          timeZone: tz,
          timeZoneName: "shortOffset",
        })
          .formatToParts(now)
          .find((p) => p.type === "timeZoneName")?.value ?? "";
      return { value: tz, label: `${tz.replace(/_/g, " ")} (${offset})` };
    });
  }, []);

  // Default to browser timezone if no value set
  useEffect(() => {
    if (!value || value === "UTC") {
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz && timezones.some((tz) => tz.value === browserTz)) {
        onChange(browserTz);
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <SearchableSelect
      value={value}
      onChange={onChange}
      options={timezones}
      searchPlaceholder="Search timezones..."
    />
  );
}
